import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  TextField
} from '@mui/material';
import {
  DirectionsCar,
  People,
  GpsFixed,
  Email,
  Refresh
} from '@mui/icons-material';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { PieChart, BarChart } from '@mui/x-charts';
import * as dashboardApi from '../services/dashboardApi';
import DashboardSection from './DashboardSection';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Home = () => {
  // All users can access dashboard - no permission checks needed

  // Per-section state management
  // Each section has: { loading, error, errorMessage, data }
  const [summaryState, setSummaryState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: null
  });

  const [vehicleTypeState, setVehicleTypeState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: []
  });

  const [userRoleState, setUserRoleState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: []
  });

  const [vehiclesPerUserState, setVehiclesPerUserState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: []
  });

  const [contactTrendState, setContactTrendState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: null
  });

  const [commandStatsState, setCommandStatsState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: null
  });

  const [verificationState, setVerificationState] = useState({
    loading: true,
    error: null,
    errorMessage: null,
    data: []
  });

  // Date filter for verification chart
  const [verificationStartDate, setVerificationStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to last 7 days
    return date.toISOString().split('T')[0];
  });
  const [verificationEndDate, setVerificationEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    // Fetch all dashboard data
    fetchDashboardData();
  }, []);

  const fetchVerificationData = async () => {
    setVerificationState(prev => ({ ...prev, loading: true }));

    const result = await dashboardApi.getVerificationStats(
      verificationStartDate,
      verificationEndDate
    );

    if (result.success) {
      setVerificationState({
        loading: false,
        error: null,
        errorMessage: null,
        data: result.data
      });
    } else {
      setVerificationState({
        loading: false,
        error: result.error || 'API_ERROR',
        errorMessage: result.message || 'Failed to load verification statistics',
        data: []
      });
    }
  };

  // Refetch verification data when date filters change
  useEffect(() => {
    if (!verificationState.loading) {
      fetchVerificationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationStartDate, verificationEndDate]);

  const fetchDashboardData = async () => {
    // Set all sections to loading state
    setSummaryState(prev => ({ ...prev, loading: true }));
    setVehicleTypeState(prev => ({ ...prev, loading: true }));
    setUserRoleState(prev => ({ ...prev, loading: true }));
    setVehiclesPerUserState(prev => ({ ...prev, loading: true }));
    setContactTrendState(prev => ({ ...prev, loading: true }));
    setCommandStatsState(prev => ({ ...prev, loading: true }));

    // Fetch all sections independently using Promise.allSettled
    const results = await Promise.allSettled([
      dashboardApi.getDashboardSummary(),           // 0: Summary
      dashboardApi.getVehicleTypeStats(),            // 1: Vehicle Types
      dashboardApi.getUserRoleStats(),               // 2: User Roles
      dashboardApi.getVehiclesPerUser(),             // 3: Vehicles Per User
      dashboardApi.getContactMessageTrend(),         // 4: Contact Trend
      dashboardApi.getCommandStats()                 // 5: Command Stats
    ]);

    // Handle summary data (special case - returns partial data with nulls)
    if (results[0].status === 'fulfilled') {
      const summary = results[0].value;
      setSummaryState({
        loading: false,
        error: null,
        errorMessage: null,
        data: summary
      });
    } else {
      setSummaryState({
        loading: false,
        error: 'API_ERROR',
        errorMessage: 'Failed to load summary statistics',
        data: null
      });
    }

    // Handle vehicle type data
    const vehicleTypeResult = results[1].status === 'fulfilled' ? results[1].value : null;
    if (vehicleTypeResult?.success) {
      setVehicleTypeState({
        loading: false,
        error: null,
        errorMessage: null,
        data: vehicleTypeResult.data
      });
    } else {
      setVehicleTypeState({
        loading: false,
        error: vehicleTypeResult?.error || 'API_ERROR',
        errorMessage: vehicleTypeResult?.message || 'Failed to load vehicle types',
        data: []
      });
    }

    // Handle user role data
    const userRoleResult = results[2].status === 'fulfilled' ? results[2].value : null;
    if (userRoleResult?.success) {
      setUserRoleState({
        loading: false,
        error: null,
        errorMessage: null,
        data: userRoleResult.data
      });
    } else {
      setUserRoleState({
        loading: false,
        error: userRoleResult?.error || 'API_ERROR',
        errorMessage: userRoleResult?.message || 'Failed to load user roles',
        data: []
      });
    }

    // Handle vehicles per user data
    const vehiclesPerUserResult = results[3].status === 'fulfilled' ? results[3].value : null;
    if (vehiclesPerUserResult?.success) {
      setVehiclesPerUserState({
        loading: false,
        error: null,
        errorMessage: null,
        data: vehiclesPerUserResult.data
      });
    } else {
      setVehiclesPerUserState({
        loading: false,
        error: vehiclesPerUserResult?.error || 'API_ERROR',
        errorMessage: vehiclesPerUserResult?.message || 'Failed to load vehicles per user',
        data: []
      });
    }

    // Handle contact trend data
    const contactTrendResult = results[4].status === 'fulfilled' ? results[4].value : null;
    if (contactTrendResult?.success) {
      setContactTrendState({
        loading: false,
        error: null,
        errorMessage: null,
        data: contactTrendResult.data
      });
    } else {
      setContactTrendState({
        loading: false,
        error: contactTrendResult?.error || 'API_ERROR',
        errorMessage: contactTrendResult?.message || 'Failed to load contact messages',
        data: null
      });
    }

    // Handle command stats data
    const commandStatsResult = results[5].status === 'fulfilled' ? results[5].value : null;
    if (commandStatsResult?.success) {
      setCommandStatsState({
        loading: false,
        error: null,
        errorMessage: null,
        data: commandStatsResult.data
      });
    } else {
      setCommandStatsState({
        loading: false,
        error: commandStatsResult?.error || 'API_ERROR',
        errorMessage: commandStatsResult?.message || 'Failed to load command statistics',
        data: null
      });
    }

    // Fetch verification data separately (has date filters)
    await fetchVerificationData();
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  // All authenticated users can view the full dashboard
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <DashboardSection
                loading={summaryState.loading}
                error={summaryState.data?.totalVehicles === null ? 'NO_PERMISSION' : null}
                errorMessage="No bookmark.view permission"
                data={summaryState.data?.totalVehicles}
                minHeight="80px"
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Vehicles
                    </Typography>
                    <Typography variant="h4">
                      {summaryState.data?.totalVehicles ?? 0}
                    </Typography>
                  </Box>
                  <DirectionsCar sx={{ fontSize: 48, color: '#1976d2', opacity: 0.7 }} />
                </Box>
              </DashboardSection>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <DashboardSection
                loading={summaryState.loading}
                error={summaryState.data?.totalUsers === null ? 'NO_PERMISSION' : null}
                errorMessage="No user.view permission"
                data={summaryState.data?.totalUsers}
                minHeight="80px"
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Users
                    </Typography>
                    <Typography variant="h4">
                      {summaryState.data?.totalUsers ?? 0}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 48, color: '#9c27b0', opacity: 0.7 }} />
                </Box>
              </DashboardSection>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <DashboardSection
                loading={summaryState.loading}
                error={summaryState.data?.totalTrackers === null ? 'NO_PERMISSION' : null}
                errorMessage="No tracker.view permission"
                data={summaryState.data?.totalTrackers}
                minHeight="80px"
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      GPS Trackers
                    </Typography>
                    <Typography variant="h4">
                      {summaryState.data?.totalTrackers ?? 0}
                    </Typography>
                  </Box>
                  <GpsFixed sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.7 }} />
                </Box>
              </DashboardSection>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <DashboardSection
                loading={summaryState.loading}
                error={summaryState.data?.contactStats === null ? 'NO_PERMISSION' : null}
                errorMessage="No contact_message.view permission"
                data={summaryState.data?.contactStats}
                minHeight="80px"
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Contact Messages
                    </Typography>
                    <Typography variant="h4">
                      {summaryState.data?.contactStats?.total ?? 0}
                    </Typography>
                    <Typography variant="caption" color="error">
                      {summaryState.data?.contactStats?.unread ?? 0} unread
                    </Typography>
                  </Box>
                  <Email sx={{ fontSize: 48, color: '#ed6c02', opacity: 0.7 }} />
                </Box>
              </DashboardSection>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Vehicle Type Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Vehicle Type Distribution
            </Typography>
            <DashboardSection
              loading={vehicleTypeState.loading}
              error={vehicleTypeState.error}
              errorMessage={vehicleTypeState.errorMessage}
              data={vehicleTypeState.data}
              isEmpty={vehicleTypeState.data?.length === 0}
              emptyMessage="No vehicle data available"
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 300 }}>
                <PieChart
                  series={[{
                    data: vehicleTypeState.data,
                    innerRadius: 10,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 5
                  }]}
                  sx={{ maxWidth: '100%' }}
                  width={400}
                  height={300}
                />
              </Box>
            </DashboardSection>
          </Paper>
        </Grid>

        {/* User Role Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              User Role Distribution
            </Typography>
            <DashboardSection
              loading={userRoleState.loading}
              error={userRoleState.error}
              errorMessage={userRoleState.errorMessage}
              data={userRoleState.data}
              isEmpty={userRoleState.data?.length === 0}
              emptyMessage="No user data available"
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 300 }}>
                <PieChart
                  series={[{
                    data: userRoleState.data,
                    innerRadius: 10,
                    outerRadius: 100,
                    paddingAngle: 2,
                    cornerRadius: 5
                  }]}
                  sx={{ maxWidth: '100%' }}
                  width={400}
                  height={300}
                />
              </Box>
            </DashboardSection>
          </Paper>
        </Grid>

        {/* Vehicles Per User */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Users by Vehicle Count
            </Typography>
            <DashboardSection
              loading={vehiclesPerUserState.loading}
              error={vehiclesPerUserState.error}
              errorMessage={vehiclesPerUserState.errorMessage}
              data={vehiclesPerUserState.data}
              isEmpty={vehiclesPerUserState.data?.length === 0}
              emptyMessage="No user vehicle data available"
            >
              <Box sx={{ height: 300 }}>
                <BarChart
                  dataset={vehiclesPerUserState.data}
                  yAxis={[{ scaleType: 'band', dataKey: 'username' }]}
                  series={[{ dataKey: 'count', label: 'Vehicles' }]}
                  layout="horizontal"
                  height={300}
                />
              </Box>
            </DashboardSection>
          </Paper>
        </Grid>

        {/* Contact Message Trend */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Contact Messages (Last 7 Days)
            </Typography>
            <DashboardSection
              loading={contactTrendState.loading}
              error={contactTrendState.error}
              errorMessage={contactTrendState.errorMessage}
              data={contactTrendState.data}
              isEmpty={!contactTrendState.data}
              emptyMessage="No contact message data available"
            >
              {contactTrendState.data && (
                <Box sx={{ height: 300 }}>
                  <Line
                    data={{
                      labels: contactTrendState.data.labels,
                      datasets: [{
                        label: 'Messages',
                        data: contactTrendState.data.data,
                        borderColor: 'rgba(75, 192, 192, 1)',
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        fill: true,
                        tension: 0.4
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </DashboardSection>
          </Paper>
        </Grid>

        {/* GPS Command Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              GPS Command Activity per Tracker
            </Typography>
            <DashboardSection
              loading={commandStatsState.loading}
              error={commandStatsState.error}
              errorMessage={commandStatsState.errorMessage}
              data={commandStatsState.data}
              isEmpty={!commandStatsState.data || Object.keys(commandStatsState.data?.commandsPerTracker || {}).length === 0}
              emptyMessage="No GPS command data available"
            >
              {commandStatsState.data && (
                <Box sx={{ height: 300 }}>
                  <Bar
                    data={{
                      labels: Object.keys(commandStatsState.data.commandsPerTracker || {}),
                      datasets: [{
                        label: 'Commands Sent',
                        data: Object.values(commandStatsState.data.commandsPerTracker || {}),
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </DashboardSection>
          </Paper>
        </Grid>

        {/* Vehicle Verification Statistics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
              <Typography variant="h6">
                Vehicle Verification Activity
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={verificationStartDate}
                  onChange={(e) => setVerificationStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={verificationEndDate}
                  onChange={(e) => setVerificationEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                />
              </Box>
            </Box>
            <DashboardSection
              loading={verificationState.loading}
              error={verificationState.error}
              errorMessage={verificationState.errorMessage}
              data={verificationState.data}
              isEmpty={verificationState.data?.length === 0}
              emptyMessage="No verification data available for the selected date range"
            >
              {verificationState.data && verificationState.data.length > 0 && (
                <Box sx={{ height: 300 }}>
                  <Bar
                    data={{
                      labels: verificationState.data.map(d => {
                        const date = new Date(d.date);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }),
                      datasets: [
                        {
                          label: 'Custom ID',
                          data: verificationState.data.map(d => d.customId),
                          backgroundColor: 'rgba(75, 192, 192, 0.6)',
                          borderColor: 'rgba(75, 192, 192, 1)',
                          borderWidth: 1,
                          stack: 'stack1'
                        },
                        {
                          label: 'VIN Number',
                          data: verificationState.data.map(d => d.vin),
                          backgroundColor: 'rgba(54, 162, 235, 0.6)',
                          borderColor: 'rgba(54, 162, 235, 1)',
                          borderWidth: 1,
                          stack: 'stack1'
                        },
                        {
                          label: 'Engine Number',
                          data: verificationState.data.map(d => d.engine),
                          backgroundColor: 'rgba(255, 206, 86, 0.6)',
                          borderColor: 'rgba(255, 206, 86, 1)',
                          borderWidth: 1,
                          stack: 'stack1'
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'top'
                        },
                        tooltip: {
                          mode: 'index',
                          intersect: false
                        }
                      },
                      scales: {
                        x: {
                          stacked: true
                        },
                        y: {
                          stacked: true,
                          beginAtZero: true,
                          ticks: {
                            stepSize: 1
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </DashboardSection>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Home;
